import React from "react";
import type { Contact } from "src/shared/contacts/objects/Contact";
import { CountryCodeName } from "src/shared/formatters/CountryFormatter";
import { Formatter } from "src/shared/formatters/Formatter";
import { LanguageName } from "src/shared/formatters/LanguageFormatter";
import { LinkFormatter } from "src/shared/formatters/LinkFormatter";
import { useLanguages } from "src/shared/languages/useLanguages";

interface IContactFormatterProps {
  children: Partial<Contact>;
  isCopyable?: boolean;
}

export const ContactAddress = ({
  children: contact,
  isCopyable,
}: IContactFormatterProps) => {
  return (
    <>
      <Formatter isCopyable={isCopyable}>{contact.address1}</Formatter>
      <br />
      {contact.address2 && (
        <>
          <Formatter isCopyable={isCopyable}>{contact.address2}</Formatter>
          <br />
        </>
      )}
      {!isCopyable ? (
        <>
          <Formatter>{contact.city}</Formatter>
          {contact.regionCode && (
            <>
              , <Formatter>{contact.regionCode}</Formatter>
            </>
          )}
        </>
      ) : (
        <>
          <Formatter isCopyable>{contact.city}</Formatter>
          <br />
          {contact.regionCode && (
            <Formatter isCopyable>{contact.regionCode}</Formatter>
          )}
        </>
      )}
      <br />
      <Formatter isCopyable={isCopyable}>{contact.postalCode}</Formatter>
      <br />
      <ContactCountry isCopyable={isCopyable}>{contact}</ContactCountry>
    </>
  );
};

export const ContactContactName = ({
  children: contact,
  isCopyable,
}: IContactFormatterProps) => {
  const value =
    `${contact.contactFirstName ?? ""} ${contact.contactLastName ?? ""}`.trim();
  return <Formatter isCopyable={isCopyable}>{value}</Formatter>;
};

export const ContactLanguage = ({
  children: contact,
}: IContactFormatterProps) => {
  const { data: languages } = useLanguages(contact.countryCode);
  const language = languages?.find(
    (lang) => lang.languageCode === contact.languageCode,
  );
  if (!language) {
    return <Formatter>{null}</Formatter>;
  }
  return <LanguageName>{language}</LanguageName>;
};

export const ContactCountry = ({
  children: contact,
  isCopyable,
}: IContactFormatterProps) => {
  if (!contact.countryCode) {
    return <Formatter>{null}</Formatter>;
  }
  return (
    <CountryCodeName isCopyable={isCopyable}>
      {contact.countryCode}
    </CountryCodeName>
  );
};

export const ContactWebsite = ({
  children: contact,
  isCopyable,
}: IContactFormatterProps) => {
  if (!contact.webSite) {
    return <Formatter>{null}</Formatter>;
  }
  return (
    <LinkFormatter isCopyable={isCopyable} inline={false}>
      {contact.webSite}
    </LinkFormatter>
  );
};

export const ContactTitle = ({ children: contact }: IContactFormatterProps) => {
  return <Formatter>{contact.contactTitle}</Formatter>;
};

export const ContactPhone = ({
  children: contact,
  isCopyable,
}: IContactFormatterProps) => {
  const value = `${contact.phoneNumber1}${contact.phoneExt1 ? ` (${contact.phoneExt1})` : ""}`;
  return (
    <Formatter value={value} isCopyable={isCopyable}>
      {value}
    </Formatter>
  );
};

export const ContactEmail = ({
  children: contact,
  isCopyable,
}: IContactFormatterProps) => {
  return <Formatter isCopyable={isCopyable}>{contact.email}</Formatter>;
};

export const ContactFax = ({
  children: contact,
  isCopyable,
}: IContactFormatterProps) => {
  return <Formatter isCopyable={isCopyable}>{contact.faxNumber}</Formatter>;
};

export const ContactMobile = ({
  children: contact,
  isCopyable,
}: IContactFormatterProps) => {
  return <Formatter isCopyable={isCopyable}>{contact.mobileNumber}</Formatter>;
};

export const ContactTimeZone = ({
  children: contact,
  isCopyable,
}: IContactFormatterProps) => {
  return <Formatter isCopyable={isCopyable}>{contact.timeZone}</Formatter>;
};
